import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as nodeUtils from "../../../abstract/controller/utils/nodeUtils";
import { setWindowLock } from "../../../abstract/redux/slice/overlaySlice";
import { handleParameterRemove } from "../../../abstract/redux/slice/structureSlice";
import IconBxsDownArrow from "../../../themes/icons/IconBxsDownArrow";
import IconBxsRightArrow from "../../../themes/icons/IconBxsRightArrow";
import Conditions from "../condition/Conditions";
import DialogSelect from "../../ui/modal/DialogSelect";
import styles from "./ParameterConditions.module.scss";
import { getIsFolded } from "../../../abstract/redux/slice/metaSlice";

// Event name for constraint logic warnings
const CONSTRAINT_WARNING_EVENT = 'constraint-warning';

function ParameterConditions({ node }) {
  const _dispatch = useDispatch();
  const [show, setShow] = useState(false);
  const [showLogicWarning, setShowLogicWarning] = useState(false);
  const [pendingRemovalId, setPendingRemovalId] = useState(null);
  
  const _name = nodeUtils.getName(node);
  const _conditions = nodeUtils.getConditions(node);
  const _parameters = nodeUtils.getParameters(node);
  const _nodeId = nodeUtils.getID(node);
  const _nodeUuid = nodeUtils.getUUID(node)

  const _isFolded = useSelector(getIsFolded(_nodeUuid));

  const handleOnClick = () => {
    setShow((e) => !e);
  };

  // Listen for constraint warning events from sub-parameter removals
  useEffect(() => {
    const handleConstraintWarning = (event) => {
      const { parentId, parameterId } = event.detail;
      
      // Check if this warning is for this parameter
      if (parentId === _nodeId) {
        const hasConditions = _conditions && _conditions.length > 0;
        const validSubParameters = _parameters?.filter(param => {
          const paramName = nodeUtils.getName(param);
          return paramName && paramName.trim().length > 0;
        }) || [];
        const isLastValidSubParameter = validSubParameters.length === 1;

        // Only show warning if there are existing conditions/aliases that would be lost
        if (hasConditions && isLastValidSubParameter) {
          _dispatch(setWindowLock(true));
          setPendingRemovalId(parameterId);
          setShowLogicWarning(true);
          
          // NEW: Signal to cancel the timeout since we're showing a modal
          const cancelTimeoutEvent = new CustomEvent('constraint-warning-cancel', {
            detail: { parameterId }
          });
          window.dispatchEvent(cancelTimeoutEvent);
          
        } else {
          // No warning needed, proceed with removal
          const proceedEvent = new CustomEvent('constraint-warning-proceed', {
            detail: { parameterId }
          });
          window.dispatchEvent(proceedEvent);
        }
      }
    };

    window.addEventListener(CONSTRAINT_WARNING_EVENT, handleConstraintWarning);
    return () => window.removeEventListener(CONSTRAINT_WARNING_EVENT, handleConstraintWarning);
  }, [_nodeId, _conditions, _parameters, _dispatch]);

  const handleConfirmRemoval = () => {
    if (pendingRemovalId) {
      // Proceed with the actual parameter removal
      _dispatch(handleParameterRemove(pendingRemovalId));
    }
    _dispatch(setWindowLock(false));
    setShowLogicWarning(false);
    setPendingRemovalId(null);
  };

  const handleCancelRemoval = () => {
    _dispatch(setWindowLock(false));
    setShowLogicWarning(false);
    setPendingRemovalId(null);
  };

  if (_isFolded || !_name) {
    return <div className={styles["container__empty"]} />;
  }

  const hasValidSubParameters =
    _parameters &&
    _parameters.some((param) => {
      const paramName = nodeUtils.getName(param);
      return paramName && paramName.trim().length > 0;
    });

  if (!hasValidSubParameters) {
    return <div className={styles["container__empty"]} />;
  }

  return (
    <div className={styles["container"]}>
      <div
        className={styles["container__checkbox"]}
        role="button"
        onClick={handleOnClick}
      >
        <div className={styles["container__checkbox__icon"]}>
          {show ? <IconBxsDownArrow /> : <IconBxsRightArrow />}
        </div>
        <div
          className={styles["container__checkbox__label"]}
        >{`Logic (${_conditions.length})`}</div>
      </div>
      <div className={styles["container__content"]}>
        {show && <Conditions node={node} />}
      </div>

      {/* Constraint Logic Warning Modal */}
      {showLogicWarning && (
        <DialogSelect
          textHeader="remove parameter"
          textBody="Removing this parameter will also remove all constraint logic (aliases and conditions) from this parameter. Do you want to continue?"
          actions={[
            {
              text: "remove",
              handler: handleConfirmRemoval,
            },
            {
              text: "cancel",
              handler: handleCancelRemoval,
            },
          ]}
          cancel={handleCancelRemoval}
        />
      )}
    </div>
  );
}

export default ParameterConditions;
